import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requireDashboardAccess?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles = [], requireDashboardAccess = false }) => {
  const { user, loading, isAuthenticated, hasRole, canAccessDashboard } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#1a1a1a',
        }}
      >
        <CircularProgress sx={{ color: '#DAA520', mb: 2 }} />
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading...</Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireDashboardAccess && !canAccessDashboard()) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#1a1a1a',
          padding: 3,
        }}
      >
        <Typography variant="h4" sx={{ color: '#DAA520', mb: 2 }}>
          Access Restricted
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
          Store managers do not have access to the dashboard.
          <br />
          Please contact Nick for assistance.
        </Typography>
      </Box>
    );
  }

  if (allowedRoles.length > 0 && !hasRole(allowedRoles)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#1a1a1a',
          padding: 3,
        }}
      >
        <Typography variant="h4" sx={{ color: '#DAA520', mb: 2 }}>
          Unauthorized
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', textAlign: 'center' }}>
          You don't have permission to access this page.
          <br />
          Required role: {allowedRoles.join(', ')}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
